import { useEffect } from "react";
import { Plane, Gauge, PlusCircle, ShieldCheck, ArrowRight, Phone } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/lang";

const Tarifs = () => {
  const { t } = useLang();
  const tf = t.tarifs || {};

  useEffect(() => {
    document.title = t.lang === "en"
      ? "Taxi Fares Paris 2026 — Kenny Cab | Official Airport Flat Rates"
      : "Tarifs Taxi Paris 2026 — Kenny Cab | Forfaits Aéroports Officiels";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", tf.sub || "");
    window.scrollTo(0, 0);
  }, [t]);

  return (
    <div data-testid="tarifs-page" className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-32 pb-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] font-mono text-amber-700 mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-amber-600/60 inline-block" />
            {tf.eyebrow}
          </p>
          <h1 data-testid="tarifs-title" className="font-serif text-3xl sm:text-4xl lg:text-6xl tracking-tight text-ivory leading-[1.05] max-w-3xl">
            {tf.title}
          </h1>
          <p data-testid="tarifs-sub" className="mt-6 text-base text-zinc-600 max-w-xl leading-relaxed">{tf.sub}</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-16">
          <h2 className="font-serif text-xl sm:text-2xl lg:text-4xl text-ivory flex items-center gap-4">
            <Plane size={26} strokeWidth={1.5} className="text-champagne" />
            {tf.airportsTitle}
          </h2>
          <p className="text-sm text-zinc-600 mt-3 max-w-2xl">{tf.airportsSub}</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(tf.cards || []).map((c, i) => (
              <div key={i} data-testid={`airport-card-${i}`}
                className="bg-surface border border-amber-600/15 hover:border-amber-600/50 rounded-lg p-6 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] transition-all duration-500">
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{c.airport}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-amber-700 mt-1">{c.side}</div>
                <div className="font-serif text-5xl text-champagne mt-4">{c.price}</div>
                <div className="text-xs text-zinc-500 mt-3 leading-relaxed">{c.zones}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-10">
          <Reveal delay={0.1}>
            <h2 className="font-serif text-xl sm:text-2xl text-ivory flex items-center gap-4">
              <Gauge size={22} strokeWidth={1.5} className="text-champagne" />
              {tf.meterTitle}
            </h2>
            <p className="text-sm text-zinc-600 mt-3">{tf.meterSub}</p>
            <div data-testid="meter-table" className="mt-6 bg-surface border border-amber-600/15 rounded-lg overflow-hidden">
              {(tf.meter || []).map((m, i) => (
                <div key={i} className={`flex items-center justify-between px-6 py-4 ${i % 2 ? "bg-surface-hover" : ""}`}>
                  <span className="text-sm text-zinc-700">{m.label}</span>
                  <span className="font-mono text-base text-champagne font-semibold whitespace-nowrap">{m.value}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <h2 className="font-serif text-xl sm:text-2xl text-ivory flex items-center gap-4">
              <PlusCircle size={22} strokeWidth={1.5} className="text-champagne" />
              {tf.suppTitle}
            </h2>
            <p className="text-sm text-zinc-600 mt-3">{tf.suppSub}</p>
            <div data-testid="supplements-table" className="mt-6 bg-surface border border-amber-600/15 rounded-lg overflow-hidden">
              {(tf.supp || []).map((s, i) => (
                <div key={i} className={`flex items-center justify-between px-6 py-4 ${i % 2 ? "bg-surface-hover" : ""}`}>
                  <span className="text-sm text-zinc-700">{s.label}</span>
                  <span className="font-mono text-base text-champagne font-semibold whitespace-nowrap">{s.value}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-16">
          <div className="bg-surface border border-amber-600/20 rounded-lg p-8 sm:p-10">
            <h2 className="font-serif text-xl sm:text-2xl text-ivory flex items-center gap-4">
              <ShieldCheck size={22} strokeWidth={1.5} className="text-champagne" />
              {tf.guaranteesTitle}
            </h2>
            <ul data-testid="guarantees-list" className="mt-6 grid sm:grid-cols-2 gap-4">
              {(tf.guarantees || []).map((g, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-700">
                  <span className="text-champagne mt-0.5">✦</span> {g}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-4">
              <a data-testid="tarifs-cta-btn" href="/#booking"
                className="group flex items-center gap-3 bg-champagne text-white text-sm font-semibold uppercase tracking-[0.15em] rounded-full px-8 py-4 hover:bg-champagne-bright hover:shadow-[0_0_30px_rgba(154,123,36,0.25)] transition-all duration-300">
                {tf.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a data-testid="tarifs-call-btn" href="tel:+33651884802"
                className="flex items-center gap-3 text-sm uppercase tracking-[0.15em] text-zinc-700 border border-amber-600/30 rounded-full px-8 py-4 hover:border-amber-600/70 hover:text-champagne transition-colors duration-300">
                <Phone size={15} /> +33 6 51 88 48 02
              </a>
            </div>
          </div>
        </Reveal>
      </main>
      <Footer />
    </div>
  );
};

export default Tarifs;
